import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
  Drawer,
  IconButton,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  SmartToy as BotIcon,
  TrendingUp as TrendingIcon,
  People as PeopleIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { useAppSelector } from '../../../store/hooks';
import { useChat } from '../hooks/useChat';
import ChatList from '../components/ChatList';
import ChatWindow from '../components/ChatWindow';
import { ChatMetrics } from '../types';

const WhatsAppDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // État local
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Redux state
  const selectedAgent = useAppSelector(state => state.agents.selectedAgent);
  const agents = useAppSelector(state => state.agents.agents);
  
  // Chat hooks
  const { chats, isLoading } = useChat();

  // Calculs des métriques
  const metrics: ChatMetrics = {
    totalChats: chats.length,
    activeChats: chats.filter(c => c.status === 'active').length,
    averageResponseTime: 1.2, // Sera calculé depuis l'API
    messagesPerHour: 24, // Sera calculé depuis l'API
    aiAccuracy: 87.5, // Sera calculé depuis l'API
    customerSatisfaction: 4.2 // Sera calculé depuis l'API
  };

  const handleChatSelect = (chatId: string) => {
    setSelectedChatId(chatId);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const handleNewChat = () => {
    // TODO: Implémenter création de nouvelle conversation
    console.log('Nouvelle conversation');
  };

  const handleSettingsClick = () => {
    setSettingsOpen(true);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const MetricsCard = ({ title, value, subtitle, icon, color = 'primary' }: any) => (
    <Card>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: `${color}.light`,
            color: `${color}.contrastText`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight="bold">
            {value}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const sidebarContent = (
    <Box
      sx={{
        width: isMobile ? '100vw' : 380,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {isMobile && (
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar>
            <Typography variant="h6" sx={{ flex: 1 }}>
              WhatsApp
            </Typography>
            <IconButton onClick={toggleSidebar}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}
      
      {/* Métriques rapides */}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {metrics.activeChats}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Actives
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="secondary" fontWeight="bold">
                {metrics.totalChats}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Agent sélectionné */}
        {selectedAgent && (
          <Card sx={{ mt: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BotIcon color="primary" />
                <Typography variant="subtitle2" fontWeight="medium">
                  Agent actif
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {selectedAgent.name}
              </Typography>
              <Chip
                size="small"
                label={selectedAgent.status}
                color={selectedAgent.status === 'active' ? 'success' : 'default'}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Liste des chats */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <ChatList
          selectedChatId={selectedChatId}
          onChatSelect={handleChatSelect}
          onNewChat={handleNewChat}
        />
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        // The global layout already owns the page header and footer.
        height: 'calc(100vh - 128px)',
        minHeight: 560,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header mobile */}
      {isMobile && (
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar>
            <IconButton onClick={toggleSidebar} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1 }}>
              WhatsApp Business
            </Typography>
            {selectedChatId && (
              <IconButton onClick={handleSettingsClick}>
                <BotIcon />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
        {/* Sidebar desktop ou drawer mobile */}
        {isMobile ? (
          <Drawer
            variant="temporary"
            anchor="left"
            open={sidebarOpen}
            onClose={toggleSidebar}
            ModalProps={{ keepMounted: true }}
          >
            {sidebarContent}
          </Drawer>
        ) : (
          <Paper 
            elevation={1} 
            sx={{ 
              width: 380, 
              minHeight: 0,
              display: 'flex',
              borderRadius: 0,
              borderRight: 1,
              borderColor: 'divider'
            }}
          >
            {sidebarContent}
          </Paper>
        )}

        {/* Zone principale */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {selectedChatId ? (
            <ChatWindow
              chatId={selectedChatId}
              onSettingsClick={handleSettingsClick}
            />
          ) : (
            /* Vue d'accueil sans conversation sélectionnée */
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              gap: 4
            }}>
              {/* Métriques générales */}
              <Box sx={{ width: '100%', maxWidth: 800 }}>
                <Typography variant="h4" gutterBottom textAlign="center">
                  WhatsApp Business Dashboard
                </Typography>
                
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <MetricsCard
                      title="Conversations"
                      value={metrics.totalChats}
                      subtitle={`${metrics.activeChats} actives`}
                      icon={<MessageIcon />}
                      color="primary"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <MetricsCard
                      title="Agents IA"
                      value={agents.length}
                      subtitle={`${agents.filter(a => a.status === 'active').length} en ligne`}
                      icon={<BotIcon />}
                      color="secondary"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <MetricsCard
                      title="Temps moyen"
                      value={`${metrics.averageResponseTime}s`}
                      subtitle="Réponse"
                      icon={<TrendingIcon />}
                      color="success"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <MetricsCard
                      title="Précision IA"
                      value={`${metrics.aiAccuracy}%`}
                      subtitle="Satisfaction client"
                      icon={<PeopleIcon />}
                      color="info"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Instructions */}
              <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
                <Typography variant="h6" gutterBottom>
                  Bienvenue dans votre centre de messagerie
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Sélectionnez une conversation dans la liste de gauche pour commencer à échanger avec vos clients. 
                  Vos agents IA prennent automatiquement en charge les demandes.
                </Typography>
                
                {!isMobile && (
                  <Typography variant="body2" color="text.secondary">
                    💡 Astuce: Utilisez les raccourcis clavier pour naviguer plus rapidement
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

    </Box>
  );
};

export default WhatsAppDashboard;
